import { z } from 'zod';
import {
  IdSchema,
  TimestampsSchema,
  booleanQueryParam,
  paginated,
  softBoolean,
  softString,
} from './common.schema';

// Estado de tours persistido por user. `seen` mapea cada key de ruta
// (`/`, `/products/:sku`, etc.) a `true` cuando el user ya vio el tour
// de esa ruta. `neverShow=true` silencia todos los tours globalmente.
// Defaults tolerantes: si el back devuelve `{}` o no manda el campo,
// el tour onboarding se considera no-visto y se dispara.
export const TourStateSchema = z
  .object({
    seen: z.record(z.string(), z.literal(true)).optional().default({}),
    neverShow: z.boolean().optional().default(false),
  })
  .default({ seen: {}, neverShow: false });

export type TourState = z.infer<typeof TourStateSchema>;

// Catálogo de permisos del release 2026-08-24. Enum estable — el back
// puede sumar valores futuros, tolerantes vía `z.string()` fallback.
// Fuente autoritativa: `GET /admin/permissions.permissions`.
export const PERMISSION_VALUES = [
  'products:read',
  'products:write',
  'products:delete',
  'media:write',
  // Solo subir imágenes nuevas: no reemplaza, no oculta ni borra las existentes.
  'media:upload',
  'ai:curate',
  'ai:configure',
  'clients:read',
  'clients:write',
  'areas:configure',
  'users:manage',
  'roles:manage',
  'webhooks:operate',
  'ingest:replay',
  'audit:read',
  'areas:bypass',
  'sources:inspect',
  // Release 2026-08-26: CRUD del catálogo de integradores. La membership
  // per-integrador va por join table `integrator_role`, no por permission
  // sintetizada — evita deploy de la const cada vez que se suma un partner.
  'integrations:manage',
  // Release 2026-09-09: CRUD del catálogo de modelos IA (`/api/v1/ai-models`),
  // que además es el allowlist contra el que se valida `ai_attribute_config.model`.
  // El GET del catálogo acepta este permiso O `ai:configure`, para que quien
  // configura un campo IA pueda poblar el desplegable sin poder editar precios.
  'ai-models:manage',
  // Anunciados 2026-09-14: lectura de usuarios y roles sin poder editarlos.
  // En el front `users:manage` también cumple `users:read`, y `roles:manage`
  // o `users:manage` cumplen `roles:read` (ver `USERS_READ`/`ROLES_READ` en
  // `src/lib/permissions.ts`).
  'users:read',
  'roles:read',
  // Reglas de activación (contrato 2026-10): `read` ve reglas, campos,
  // historial, simulaciones, barridos y apagados; `manage` además guarda el
  // borrador, simula, activa, desactiva, relanza y cancela barridos. En el
  // front `manage` también cumple la lectura (ver `ACTIVATION_RULES_READ`).
  'activation-rules:read',
  'activation-rules:manage',
] as const;
export type Permission = (typeof PERMISSION_VALUES)[number];
export const PermissionSchema = z.union([
  z.enum(PERMISSION_VALUES),
  z.string(), // Fallback tolerante — el back puede sumar permisos nuevos.
]);

// Backend entity: User. Read tolerante a campos faltantes.
//
// Con Effetá Apps, `roleSlug`/`roleId` e `isActive` son los de Effetá
// (`isActive: false` = todavía no aceptó la invitación o le quitaron el
// acceso; el PIM no distingue los dos casos) y `areaSlugs` son las del PIM.
// El enum viejo `role` (ADMIN/EDITOR/VIEWER) sigue viajando pero no dice
// nada del acceso: no se lee (Zod lo descarta).

export const UserSchema = z
  .object({
    id: IdSchema,
    email: softString,
    firstName: softString,
    lastName: softString,
    isActive: softBoolean,
    tourState: TourStateSchema.optional(),
    roleSlug: z.string().nullable().optional(),
    // `null` si el rol que la persona tiene en Effetá no es un rol del PIM.
    roleId: z.number().int().nullable().optional(),
    permissions: z.array(PermissionSchema).optional(),
    areaSlugs: z.array(z.string()).optional(),
    canBypassAreas: z.boolean().optional(),
  })
  .merge(TimestampsSchema.partial());

export type User = z.infer<typeof UserSchema>;

export const UserListSchema = paginated(UserSchema);
export type UserList = z.infer<typeof UserListSchema>;

// POST /users — dar acceso ---------------------------------------------------
//
// Da acceso al PIM a una persona: si no tiene cuenta en Effetá Apps, Effetá
// se la crea y le manda la invitación por correo. `password`, `role` e
// `isActive` no van (el back responde 422 `EFFETA_MANAGED_FIELD`).
// - email con formato y `.max(255)` (DB varchar).
// - `roleId` obligatorio: sin rol no se da acceso.
// - firstName/lastName obligatorios (1..150): Effetá los usa si la persona no
//   tiene cuenta; si ya la tiene, no los cambia.
// - `areaSlugs` (máx. 50, cada slug 1..100) son del PIM. Omitido no toca las
//   que ya tuviera.
export const UserCreateSchema = z.object({
  email: z.string().email().max(255),
  roleId: z.number().int().positive(),
  firstName: z.string().min(1).max(150),
  lastName: z.string().min(1).max(150),
  areaSlugs: z.array(z.string().min(1).max(100)).max(50).optional(),
});
export type UserCreate = z.infer<typeof UserCreateSchema>;

// Respuesta 201: la persona más qué hizo Effetá. Opcionales a propósito: si
// faltaran, el alta igual salió bien y no hay que convertirla en error.
// - `revived`: reutilizó la fila de un usuario borrado del PIM con ese email.
// - `accountCreated`: Effetá le creó la cuenta (no tenía).
// - `invitationSent`: Effetá le mandó la invitación por correo.
export const UserCreatedSchema = UserSchema.extend({
  revived: z.boolean().optional(),
  accountCreated: z.boolean().optional(),
  invitationSent: z.boolean().optional(),
});
export type UserCreated = z.infer<typeof UserCreatedSchema>;

// Filtros para listado -----------------------------------------------------

export const UserListParamsSchema = z.object({
  search: z.string().optional(),
  isActive: booleanQueryParam.optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  skip: z.coerce.number().int().min(0).default(0),
  take: z.coerce.number().int().min(1).max(200).default(50),
});
export type UserListParams = z.infer<typeof UserListParamsSchema>;
